
import { AIService } from './interface';
import { LaborRole, Quote, LaborStep, MaterialLine, PriceListItem, AIBrief, AIActionProposal, SocialMediaDraft, RFQDraft, ProposalSettings, OutreachDraft, Customer, CompanyInfo, LeadScore, CaseStudy, ProjectFinancialHealth, Invoice, MaintenanceChecklist, ServiceReport } from "../../types";
import { logger } from '../../utils/logger';
import { callAIProxy, isAIProxyAvailable } from './proxyClient';
import { generateId } from '../../utils/idGenerator';
import { getTodayLocalDate } from '../../utils/scheduleDates';

// Type enum for schema definitions (compatibility with old API)
const Type = {
  STRING: 'string',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  ARRAY: 'array',
  OBJECT: 'object'
} as const;

const DEFAULT_GEMINI_MODEL = 'gemini-2.0-flash';

class GoogleGenerativeAI {
  constructor(_apiKey?: string) {}
  models = {
    generateContent: async (payload: any) => {
      if (!isAIProxyAvailable()) {
        throw new Error('AI proxy not configured. Set VITE_AI_WORKER_URL and Workers secrets.');
      }

      const { model, contents, config } = payload || {};
      const response = await callAIProxy('gemini', 'generateContent', {
        contents,
        config: {
          ...(config || {}),
          model,
        },
      });

      if (!response) {
        throw new Error('AI proxy failed. Please check Workers secrets and deployment.');
      }

      const text = typeof response === 'string' ? response : JSON.stringify(response);
      return { text };
    },
  };
}

// Initialize default prompts lazily (don't block module loading)
let promptsInitialized = false;
const initPromptsOnce = async () => {
  if (!promptsInitialized) {
    promptsInitialized = true;
    try {
      const { initializeDefaultPrompts } = await import('../prompts/defaultPrompts');
      await initializeDefaultPrompts();
    } catch (e) {
      console.warn('Failed to initialize default prompts:', e);
    }
  }
};

// Start initialization in background (non-blocking)
initPromptsOnce().catch(() => {});

export class GeminiProvider implements AIService {
  private model: string = DEFAULT_GEMINI_MODEL;
  
  /**
   * Set the model to use for API calls
   */
  setModel(model: string): void {
    this.model = model;
  }
  
  /**
   * Get the current model
   */
  getModel(): string {
    return this.model;
  }
  
  private getApiKey() {
    if (!isAIProxyAvailable()) {
      return null;
    }

    return 'proxy';
  }

  private async retryOperation<T>(operation: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
    try {
      return await operation();
    } catch (error: any) {
      const isOverloaded = error?.message?.includes('503') || error?.status === 503 || error?.message?.includes('overloaded');
      if (retries > 0 && isOverloaded) {
        console.warn(`Model overloaded. Retrying in ${delay}ms... (${retries} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.retryOperation(operation, retries - 1, delay * 2);
      }
      throw error;
    }
  }

  // ... (Previous methods remain unchanged) ...
  async generateScopeSuggestions(textContext: string, existingRoles: LaborRole[]): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) {
        console.error("AI proxy not configured");
        return null;
    }
    const ai = new GoogleGenerativeAI(apiKey);
    
    const roleNames = existingRoles.map(r => r.name).join(", ");
    
    // Get learned patterns to enhance the prompt
    let learningContext = '';
    try {
      const { AILearningService } = await import('../aiLearningService');
      learningContext = AILearningService.buildLearningContext(
        'SCOPE_EXTRACTION',
        textContext.substring(0, 200), // Use first 200 chars for matching
        { roleNames, textLength: textContext.length }
      );
    } catch (e) {
      // Learning service not available, continue without it
    }
    
    // Try to use PromptManager, fallback to hardcoded prompt
    let prompt = '';
    try {
      const { PromptManager } = await import('../prompts/promptManager');
      const rendered = await PromptManager.getPrompt(
        'SCOPE_EXTRACTION',
        'GEMINI',
        {
          roleNames,
          textContext,
          learningContext: learningContext || undefined,
        }
      );
      if (rendered) {
        prompt = rendered.userPrompt;
      }
    } catch (e) {
      console.warn('PromptManager not available, using hardcoded prompt:', e);
    }
    
    // Fallback to hardcoded prompt if PromptManager failed
    if (!prompt) {
      prompt = `
      You are an expert industrial estimator. Analyze the following bid document text.
      Extract distinct Scope Items (Bid Items).
      For each item, suggest a title, a brief description, and a list of labor steps needed.
      Available Labor Roles: ${roleNames}.
      ${learningContext ? `\n\n${learningContext}\n\n` : ''}
      Return JSON.
      
      Bid Document Text:
      ${textContext}
    `;
    }

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                code: { type: Type.STRING },
                title: { type: Type.STRING },
                description: { type: Type.STRING },
                suggestedSteps: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: { description: { type: Type.STRING }, role: { type: Type.STRING }, hours: { type: Type.NUMBER } }
                  }
                }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async generateScopeSuggestionsFromDocument(base64Data: string, mimeType: string, existingRoles: LaborRole[]): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) return null;
    const ai = new GoogleGenerativeAI(apiKey);
    const roleNames = existingRoles.map(r => r.name).join(", ");

    const prompt = `Extract distinct Scope Items (Bid Items) from this document. Suggest title, description, and labor steps. Available Labor Roles: ${roleNames}. Return JSON.`;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: { parts: [{ inlineData: { mimeType, data: base64Data } }, { text: prompt }] },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                code: { type: Type.STRING },
                title: { type: Type.STRING },
                description: { type: Type.STRING },
                suggestedSteps: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: { description: { type: Type.STRING }, role: { type: Type.STRING }, hours: { type: Type.NUMBER } }
                  }
                }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async suggestMaterialLines(title: string, scopeNotes: string, laborSteps: LaborStep[], currentMaterials: MaterialLine[], priceList: PriceListItem[]): Promise<any[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) return [];
    const ai = new GoogleGenerativeAI(apiKey);

    const laborContext = laborSteps.map(s => s.description).join('; ');
    const currentMatContext = currentMaterials.map(m => m.description).join('; ');
    
    // Enhanced price list context with better formatting
    const priceListContext = priceList.length > 0 
      ? priceList.map(p => `Model: "${p.modelNumber || 'N/A'}", Description: "${p.description}", Cost: $${p.unitCost}, Vendor: "${p.vendor || 'Various'}"`).join('\n')
      : 'Price list is empty. You may need to suggest generic materials.';

    // Get learned patterns
    let learningContext = '';
    try {
      const { AILearningService } = await import('../aiLearningService');
      learningContext = AILearningService.buildLearningContext(
        'MATERIAL_SUGGESTION',
        `${title} ${scopeNotes}`,
        { laborContext, currentMaterials: currentMatContext }
      );
    } catch (e) {
      // Learning service not available
    }

    const prompt = `
      I am estimating materials for an industrial job item.
      Item: "${title}"
      Scope: "${scopeNotes}"
      Labor Tasks: ${laborContext}
      Current Materials: ${currentMatContext || "None"}
      
      ⚠️ CRITICAL RULE: You MUST prioritize items from the price list below. 
      - FIRST: Search the price list for matching materials based on labor tasks and scope
      - Use EXACT model numbers, descriptions, costs, and vendors from the price list
      - Only suggest items NOT in the price list if they are absolutely necessary and clearly unavailable in the list
      - For price list matches, use the exact unitCost, modelNumber, and vendor provided
      - Estimate quantities (qty) based on the labor tasks and scope
      
      Available Price List (${priceList.length} items):
      ${priceListContext}
      
      ${learningContext ? `\n\n${learningContext}\n\n` : ''}
      
      Return JSON array with this structure:
      [
        {
          "description": "Material description (use exact from price list if matched)",
          "modelNumber": "Model number (use exact from price list if matched, or suggest if not in list)",
          "vendor": "Vendor name (use exact from price list if matched)",
          "qty": 1,
          "estimatedUnitCost": 0.00
        }
      ]
      
      IMPORTANT: If an item is in the price list, use its EXACT unitCost. Only estimate costs for items not in the price list.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                description: { type: Type.STRING },
                modelNumber: { type: Type.STRING },
                vendor: { type: Type.STRING },
                qty: { type: Type.NUMBER },
                estimatedUnitCost: { type: Type.NUMBER }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async generateLaborSteps(itemTitle: string, itemDescription: string, existingRoles: LaborRole[]): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) return [];
    const ai = new GoogleGenerativeAI(apiKey);
    const roleNames = existingRoles.map(r => r.name).join(", ");

    // Get learned patterns
    let learningContext = '';
    try {
      const { AILearningService } = await import('../aiLearningService');
      learningContext = AILearningService.buildLearningContext(
        'LABOR_STEPS',
        `${itemTitle} ${itemDescription}`,
        { roleNames }
      );
    } catch (e) {
      // Learning service not available
    }

    const basePrompt = `Item: "${itemTitle}". Desc: "${itemDescription}". Break down labor steps using: ${roleNames}.`;
    const fullPrompt = learningContext 
      ? `${basePrompt}\n\n${learningContext}\n\nReturn JSON.`
      : `${basePrompt} Return JSON.`;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: fullPrompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
               type: Type.OBJECT,
               properties: { description: { type: Type.STRING }, role: { type: Type.STRING }, hours: { type: Type.NUMBER } }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async generateScopeFromLabor(title: string, laborSteps: LaborStep[], materialLines: MaterialLine[], existingRoles: LaborRole[], style: 'BULLET' | 'BRIEF' | 'DETAILED'): Promise<string> {
    const apiKey = this.getApiKey();
    if (!apiKey) return "";
    const ai = new GoogleGenerativeAI(apiKey);
    
    // Get style instructions from WritingStyleService
    let styleInstructions = '';
    try {
      const { WritingStyleService } = await import('../prompts/writingStyleService');
      styleInstructions = await WritingStyleService.getInstructionsFromLegacyName(style, 'SCOPE_WRITING');
    } catch (e) {
      // Fallback to simple instructions if style service not available
      const fallbacks: Record<string, string> = {
        BRIEF: 'Write a brief, concise description.',
        BULLET: 'Write as a bullet-point list.',
        DETAILED: 'Write a detailed, comprehensive description.',
      };
      styleInstructions = fallbacks[style] || fallbacks.BRIEF;
    }
    
    // Get learned patterns
    let learningContext = '';
    try {
      const { AILearningService } = await import('../aiLearningService');
      learningContext = AILearningService.buildLearningContext(
        'SCOPE_WRITING',
        title,
        { style, laborStepsCount: laborSteps.length, materialsCount: materialLines.length }
      );
    } catch (e) {
      // Learning service not available
    }
    
    const stepsText = laborSteps.map(s => s.description).join('; ');
    const materialsText = materialLines.map(m => m.description).filter(Boolean).join('; ');
    
    const basePrompt = `Write scope description for "${title}".

Labor Steps: ${stepsText}
${materialsText ? `Materials: ${materialsText}` : ''}

Style Instructions: ${styleInstructions}

Return plain text only, no markdown formatting.`;
    
    const prompt = learningContext 
      ? `${basePrompt}\n\n${learningContext}`
      : basePrompt;

    try {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
      });
      return response.text.trim();
    } catch (e) { return ""; }
  }

  async checkScopeConsistency(title: string, scopeNotes: string, laborSteps: LaborStep[], materialLines: MaterialLine[], existingRoles: LaborRole[]): Promise<any[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) return [];
    const ai = new GoogleGenerativeAI(apiKey);

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: `Check consistency between scope "${scopeNotes}" and labor "${laborSteps.length} steps". Return JSON issues.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
              type: Type.ARRAY,
              items: {
                  type: Type.OBJECT,
                  properties: { type: { type: Type.STRING, enum: ["MISSING_COST", "MISSING_SCOPE"] }, message: { type: Type.STRING } }
              }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async parseBidFormDocument(base64Data: string, mimeType: string): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) return null;
    const ai = new GoogleGenerativeAI(apiKey);

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: { parts: [{ inlineData: { mimeType, data: base64Data } }, { text: "Extract bid form lines as ID and Label. Return JSON." }] },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: { type: Type.OBJECT, properties: { id: { type: Type.STRING }, label: { type: Type.STRING } } }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async parseVendorQuote(base64Data: string, mimeType: string): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) return { items: [] };
    const ai = new GoogleGenerativeAI(apiKey);

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: { parts: [{ inlineData: { mimeType, data: base64Data } }, { text: `Extract vendor quote line items (Model, Desc, Qty, UnitPrice, LeadTimeWeeks). Return JSON.` }] },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              vendorName: { type: Type.STRING },
              items: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    modelNumber: { type: Type.STRING }, description: { type: Type.STRING }, qty: { type: Type.NUMBER }, unitPrice: { type: Type.NUMBER }, leadTimeWeeks: { type: Type.NUMBER }
                  }
                }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async parseSupplierInvoice(base64Data: string, mimeType: string): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) return { items: [] };
    const ai = new GoogleGenerativeAI(apiKey);

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: { parts: [{ inlineData: { mimeType, data: base64Data } }, { text: `Extract supplier invoice lines (Vendor, Inv#, Date, Items: Model, Desc, Qty, Cost). Return JSON.` }] },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              vendorName: { type: Type.STRING }, invoiceNumber: { type: Type.STRING }, invoiceDate: { type: Type.STRING },
              items: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: { modelNumber: { type: Type.STRING }, description: { type: Type.STRING }, qty: { type: Type.NUMBER }, unitCost: { type: Type.NUMBER }, totalCost: { type: Type.NUMBER } }
                }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async parsePriceListDocument(base64Data: string, mimeType: string): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) return { items: [] };
    const ai = new GoogleGenerativeAI(apiKey);

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: { parts: [{ inlineData: { mimeType, data: base64Data } }, { text: `Extract price list items. Return JSON.` }] },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              vendorName: { type: Type.STRING },
              items: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: { modelNumber: { type: Type.STRING }, description: { type: Type.STRING }, unitCost: { type: Type.NUMBER } }
                }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async analyzeQuoteRisks(quote: Quote): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) return [];
    const ai = new GoogleGenerativeAI(apiKey);
    const summary = JSON.stringify({ project: quote.projectName, items: quote.bidItems.map(i => ({ t: i.title, n: i.notes, h: i.laborSteps.length })) });

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: `Analyze for risks: ${summary}. Return JSON list.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: { severity: { type: Type.STRING, enum: ["HIGH", "MEDIUM", "LOW"] }, title: { type: Type.STRING }, description: { type: Type.STRING }, recommendation: { type: Type.STRING } }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async generateMorningBrief(context: string): Promise<AIBrief> {
    if (!isAIProxyAvailable()) {
      throw new Error('AI proxy not configured. Set VITE_AI_WORKER_URL and Workers secrets.');
    }

    try {
      console.log('Using AI proxy for generateMorningBrief');
      const body = {
        contents: `You are an expert Construction Business Coach. Digest the business data below and create a "Morning Brief".\n\nCONTEXT:\n${context}\n\nReturn JSON.`,
        config: {
          model: this.model,
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              date: { type: Type.STRING },
              topPriorities: { type: Type.ARRAY, items: { type: Type.STRING } },
              upcomingDeadlines: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { title: {type: Type.STRING}, date: {type: Type.STRING}, daysLeft: {type: Type.NUMBER} } } },
              risks: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { severity: {type: Type.STRING, enum: ['HIGH', 'MEDIUM', 'LOW']}, message: {type: Type.STRING} } } },
              suggestedSchedule: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { time: {type: Type.STRING}, task: {type: Type.STRING} } } },
              quickWins: { type: Type.ARRAY, items: { type: Type.STRING } },
              followUps: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { contact: {type: Type.STRING}, context: {type: Type.STRING}, draftMessage: {type: Type.STRING} } } },
              healthNudges: { type: Type.ARRAY, items: { type: Type.STRING } },
            }
          }
        }
      };
      
      const response = await callAIProxy('gemini', 'generateContent', body);
      if (response) {
        // Proxy should return parsed JSON already, but handle both cases
        let parsedResponse = response;
        if (typeof response === 'string') {
          try {
            parsedResponse = JSON.parse(response);
          } catch {
            // If parsing fails, return empty structure
            parsedResponse = {};
          }
        }
        
        // Ensure all required arrays exist
        return {
          date: parsedResponse.date || new Date().toISOString(),
          topPriorities: Array.isArray(parsedResponse.topPriorities) ? parsedResponse.topPriorities : [],
          upcomingDeadlines: Array.isArray(parsedResponse.upcomingDeadlines) ? parsedResponse.upcomingDeadlines : [],
          risks: Array.isArray(parsedResponse.risks) ? parsedResponse.risks : [],
          suggestedSchedule: Array.isArray(parsedResponse.suggestedSchedule) ? parsedResponse.suggestedSchedule : [],
          quickWins: Array.isArray(parsedResponse.quickWins) ? parsedResponse.quickWins : [],
          followUps: Array.isArray(parsedResponse.followUps) ? parsedResponse.followUps : [],
          healthNudges: Array.isArray(parsedResponse.healthNudges) ? parsedResponse.healthNudges : [],
        };
      }
    } catch (error) {
      console.error('AI proxy failed:', error);
      throw new Error(`AI proxy failed: ${error instanceof Error ? error.message : 'Unknown error'}. Please check Workers secrets and function deployment.`);
    }

    throw new Error('AI proxy returned an empty response.');
  }

  async parseDailyLog(logText: string, context: string): Promise<any> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    // Try to use PromptManager, fallback to hardcoded prompt
    let prompt = '';
    try {
      const { PromptManager } = await import('../prompts/promptManager');
      const rendered = await PromptManager.getPrompt(
        'LOG_PARSING',
        'GEMINI',
        {
          logText,
          context,
        }
      );
      if (rendered) {
        prompt = rendered.userPrompt;
      }
    } catch (e) {
      console.warn('PromptManager not available, using hardcoded prompt:', e);
    }
    
    // Fallback to hardcoded prompt if PromptManager failed
    if (!prompt) {
      prompt = `
      Parse this daily log into structured data. Extract all sections and information.
      
      DAILY LOG:
      ${logText}
      
      BUSINESS CONTEXT:
      ${context}
      
      IMPORTANT: Even if the log is a simple sentence (like "Worked on internal quoting app for 6 hrs"), 
      you MUST extract work items. Look for ANY mention of work, time spent, or activities.
      
      Extract the following structured information:
      1. Date (from header or infer from "Today"/"Yesterday", default to today if not found)
      2. Location (if mentioned)
      3. Work Completed - CRITICAL: break down into individual work items with:
         - Description (extract the work description, e.g. "Worked on internal quoting app")
         - Estimated hours (MUST extract from phrases like "6 hrs", "6 hours", "for 6 hrs", "half hour" -> 0.5, "1.5 hours" -> 1.5)
         - Project name (match to context if found, otherwise null for internal work)
         - Role type (Journeyman, Engineer, etc. - optional)
         - Time range if mentioned ("8:00 a.m.", "8am-5pm")
         - If the log is a simple sentence without sections, treat the entire sentence as a work item
      4. Projects Touched - list project names and types (if mentioned)
      5. Time Summary - extract total, billable, non-billable hours (if mentioned)
      6. Tools on Hand - list all tools mentioned (if any)
      7. Issues / Constraints - list all issues (if any)
      8. Key Notes / Decisions - extract important notes (if any)
      9. Follow-Ups / To-Dos - create follow-up items with:
         - Description
         - Priority (HIGH/MEDIUM/LOW based on urgency)
         - Due date (if mentioned or default to tomorrow)
         - Linked project (if mentioned)
      10. Tomorrow's Priorities - list priorities (if mentioned)
      11. Actions - extract business actions from text:
          - "Sent Quote" → QUOTE_SENT with quote name
          - "Got approval" / "Won" → QUOTE_WON with quote name
          - "Invoiced" / "Sent invoice" → INVOICE_CREATED with amount/customer
          - "Ordered materials" → MATERIAL_ORDERED
          - "Called" / "Followed up with" → CUSTOMER_CONTACTED
          - "Finalized quote" → QUOTE_FINALIZED
      
      EXAMPLES:
      - Input: "Worked on internal quoting app for 6 hrs"
        Output: workCompleted: [{ description: "Worked on internal quoting app", hours: 6, projectName: null }]
      
      - Input: "Spent 4 hours on hospital wiring project"
        Output: workCompleted: [{ description: "Spent 4 hours on hospital wiring project", hours: 4, projectName: "hospital wiring" }]
      
      Return JSON matching this structure:
      {
        "date": "YYYY-MM-DD",
        "location": "string or null",
        "workCompleted": [
          {
            "description": "string",
            "hours": number or null,
            "projectName": "string or null",
            "role": "string or null",
            "timeRange": "string or null"
          }
        ],
        "projectsTouched": [
          {
            "name": "string",
            "type": "string or null"
          }
        ],
        "timeSummary": {
          "totalHours": number or null,
          "billableHours": number or null,
          "nonBillableHours": number or null
        },
        "tools": ["string"],
        "issues": ["string"],
        "notes": ["string"],
        "followUps": [
          {
            "description": "string",
            "dueDate": "YYYY-MM-DD or null",
            "priority": "HIGH|MEDIUM|LOW or null",
            "linkedProject": "string or null"
          }
        ],
        "tomorrowPriorities": ["string"],
        "actions": [
          {
            "type": "QUOTE_SENT|QUOTE_WON|INVOICE_CREATED|MATERIAL_ORDERED|CUSTOMER_CONTACTED|QUOTE_FINALIZED",
            "entityName": "string or null",
            "details": {},
            "requiresConfirmation": boolean
          }
        ]
      }
    `;
    }

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json"
        }
      });
      const parsed = JSON.parse(response.text);
      
      // Ensure date is set (default to today if not found)
      if (!parsed.date) {
        parsed.date = getTodayLocalDate();
      }
      
      // Fallback: If no work items were extracted but the log contains work descriptions, try to extract them
      if ((!parsed.workCompleted || parsed.workCompleted.length === 0) && logText.trim()) {
        const timePatterns = [
          /(?:worked|spent|did|completed).*?(?:for|on).*?(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)/i,
          /(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h).*?(?:on|for|working|spent).*?(.+)/i,
          /(.+?)\s+for\s+(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)/i
        ];

        for (const pattern of timePatterns) {
          const match = logText.match(pattern);
          if (match) {
            let hours: number;
            let description: string;

            if (match[1] && !isNaN(parseFloat(match[1]))) {
              hours = parseFloat(match[1]);
              description = match[2] || logText.replace(match[0], '').trim() || 'Work completed';
            } else if (match[2] && !isNaN(parseFloat(match[2]))) {
              hours = parseFloat(match[2]);
              description = match[1] || logText.replace(match[0], '').trim() || 'Work completed';
            } else {
              continue;
            }

            description = description.replace(/^(?:worked|spent|did|completed|on|for)\s+/i, '').trim();
            if (!description) description = 'Work completed';

            // Check if it's internal work
            const isInternal = /internal|admin|office|app|software|system/i.test(description) || 
                              !/(?:client|customer|project|for\s+\w+)/i.test(logText);

            parsed.workCompleted = [{
              description: description,
              hours: hours,
              projectName: isInternal ? null : undefined,
              role: null,
              timeRange: null
            }];
            break;
          }
        }
      }
      
      return parsed;
    });
  }

  /**
   * Fallback function to extract time entries from simple text patterns
   * Used when AI doesn't extract actions but work is clearly mentioned
   */
  private extractTimeEntryFallback(notes: string): AIActionProposal | null {
    // Patterns to match: "worked on X for Y hours", "spent Y hours on X", "Y hrs on X", etc.
    const timePatterns = [
      /(?:worked|spent|did|completed).*?(?:for|on).*?(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)/i,
      /(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h).*?(?:on|for|working|spent).*?(.+)/i,
      /(.+?)\s+for\s+(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)/i
    ];

    for (const pattern of timePatterns) {
      const match = notes.match(pattern);
      if (match) {
        let hours: number;
        let description: string;

        if (match[1] && !isNaN(parseFloat(match[1]))) {
          // Pattern matched hours first
          hours = parseFloat(match[1]);
          description = match[2] || notes.replace(match[0], '').trim() || 'Work completed';
        } else if (match[2] && !isNaN(parseFloat(match[2]))) {
          // Pattern matched hours second
          hours = parseFloat(match[2]);
          description = match[1] || notes.replace(match[0], '').trim() || 'Work completed';
        } else {
          continue;
        }

        // Clean up description
        description = description.replace(/^(?:worked|spent|did|completed|on|for)\s+/i, '').trim();
        if (!description) description = 'Work completed';

        // Determine if internal (no client/project name patterns)
        const hasClientPattern = /(?:client|customer|project|for\s+\w+)/i.test(notes);
        const isInternal = !hasClientPattern || /internal|admin|office|app|software|system/i.test(description);

        return {
          id: generateId(),
          type: 'CREATE_TIME_ENTRY',
          title: `Log ${hours} hour${hours !== 1 ? 's' : ''}${isInternal ? ' (Internal)' : ''}`,
          description: description,
          data: {
            hours: hours,
            clientName: isInternal ? undefined : undefined,
            isInternal: isInternal
          },
          status: 'PENDING',
          reasoning: `Extracted from text: "${notes}"`
        };
      }
    }

    return null;
  }

  async analyzeDailyCheckIn(notes: string, context: string): Promise<AIActionProposal[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const prompt = `
      I am a contractor providing a daily log.
      NOTES: "${notes}"
      BUSINESS CONTEXT: ${context}

      Extract structured actions to organize my business.
      
      RULES:
      1. ALWAYS look for work descriptions and time spent, even if not in structured sections.
         - Look for patterns like: "Worked on...", "Spent time on...", "Did...", "Completed...", "for X hours", "for X hrs"
         - Extract Duration from phrases like: "6 hrs" -> 6.0, "6 hours" -> 6.0, "half hour" -> 0.5, "1.5 hours" -> 1.5
         - Extract Project/Client Name (match to business context if found, otherwise mark as internal)
         - If no client/project is mentioned or found in context, set isInternal: true
         - Create CREATE_TIME_ENTRY actions for ALL work descriptions with time mentioned
      
      2. If the input has "COMPLETED:" section, extract time logs/tasks from there.
         - Same extraction rules as above apply
      
      3. If the input has "OUTREACH:" section, extract emails from there.
         - Create DRAFT_EMAIL actions.
      
      4. If the input has "BLOCKERS:" section, create FLAG_RISK actions.

      5. IMPORTANT: Even simple sentences like "Worked on internal quoting app for 6 hrs" should create a CREATE_TIME_ENTRY action with:
         - hours: 6
         - description: "Worked on internal quoting app"
         - isInternal: true (since no client/project mentioned)

      Return JSON Action Proposals. Always return at least one action if work/time is mentioned.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                id: { type: Type.STRING },
                type: { type: Type.STRING, enum: ['CREATE_TASK', 'DRAFT_EMAIL', 'CREATE_FOLLOWUP', 'FLAG_RISK', 'GENERAL_ADVICE', 'ADD_CUSTOMER', 'CREATE_TIME_ENTRY'] },
                title: { type: Type.STRING },
                description: { type: Type.STRING },
                // Structured Payload
                data: {
                    type: Type.OBJECT,
                    properties: {
                        emailTo: { type: Type.STRING },
                        emailSubject: { type: Type.STRING },
                        emailBody: { type: Type.STRING },
                        quoteId: { type: Type.STRING },
                        priority: { type: Type.STRING },
                        // For ADD_CUSTOMER
                        customerName: { type: Type.STRING },
                        companyName: { type: Type.STRING },
                        customerEmail: { type: Type.STRING },
                        customerPhone: { type: Type.STRING },
                        // For TIME_ENTRY
                        hours: { type: Type.NUMBER },
                        clientName: { type: Type.STRING },
                        isInternal: { type: Type.BOOLEAN }
                    }
                },
                status: { type: Type.STRING, enum: ['PENDING'] },
                reasoning: { type: Type.STRING }
              }
            }
          }
        }
      });
      const actions = JSON.parse(response.text);
      const mappedActions = actions.map((a: any) => ({ ...a, id: a.id || generateId(), status: 'PENDING' }));
      
      // Fallback: If no actions were extracted but work is mentioned, try pattern matching
      if (mappedActions.length === 0) {
        const fallbackAction = this.extractTimeEntryFallback(notes);
        if (fallbackAction) {
          return [fallbackAction];
        }
      }
      
      return mappedActions;
    });
  }

  async chatWithSecretary(history: { role: 'user' | 'assistant', content: string }[], context: string, newMessage: string): Promise<string> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    // Build conversation contents from history + new message
    const contents = [
      ...history.map(h => ({
        role: h.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: h.content }]
      })),
      { role: 'user', parts: [{ text: newMessage }] }
    ];

    // @ts-ignore - GoogleGenerativeAI API types may be outdated
    const response = await (ai as any).models?.generateContent({
      model: 'gemini-2.0-flash',
      contents,
      config: {
        systemInstruction: context ? `${context}` : 'You are a helpful AI assistant.',
      }
    });

    return response?.text || '';
  }

  async generateSocialMediaDrafts(projectContext: string, companyInfo: string): Promise<SocialMediaDraft[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const prompt = `
      You are a social media manager for an industrial contracting company (${companyInfo}).
      Generate two distinct social media posts based on the project below.

      PROJECT DATA:
      ${projectContext}

      OUTPUT REQUIREMENTS:
      1. LinkedIn:
         - Tone: Professional, technical, B2B.
         - Focus: Capabilities, problem-solving, quality, safety.
         - Audience: Engineers, Plant Managers, General Contractors.
         - Format: Clear paragraphs, professional hashtags.
      
      2. Facebook:
         - Tone: Community-focused, slightly more casual, engaging.
         - Focus: Team effort, local presence, visual appeal.
         - Audience: Local community, business owners, employees.
         - Format: Use emojis 🛠️, friendly tone.

      3. Image Suggestion: Suggest a specific photo to attach (e.g. "Close up of the PLC panel with neat wiring").

      Return JSON array.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                platform: { type: Type.STRING, enum: ['LINKEDIN', 'FACEBOOK'] },
                content: { type: Type.STRING },
                hashtags: { type: Type.ARRAY, items: { type: Type.STRING } },
                imageSuggestion: { type: Type.STRING }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async generateInvoiceDescription(notes: string[], style: 'BRIEF' | 'BULLET' | 'DETAILED'): Promise<string> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const notesText = notes.join('\n');
    const prompt = `
      Summarize the following daily work logs into a professional customer-facing invoice description of work performed.
      STYLE: ${style}
      
      WORK LOGS:
      ${notesText}
      
      Output ONLY the description text.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
      });
      return response.text.trim();
    });
  }

  async generateRFQDrafts(quote: Quote, materials: MaterialLine[]): Promise<RFQDraft[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const matList = materials.map(m => `- ${m.qty}x ${m.modelNumber || 'N/A'} - ${m.description} (Vendor: ${m.vendor || 'Any'})`).join('\n');

    const prompt = `
      You are a procurement agent for ${quote.clientName}'s project: ${quote.projectName}.
      Draft RFQ emails for vendors based on this material list.
      Group materials by their specified Vendor. If no vendor is specified, group them into a 'General Electrical Supply' bucket.
      
      MATERIAL LIST:
      ${matList}

      For each vendor group, write a professional email asking for:
      1. Pricing
      2. Lead Time
      3. Availability

      Return JSON array.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                vendorName: { type: Type.STRING },
                emailSubject: { type: Type.STRING },
                emailBody: { type: Type.STRING },
                itemCount: { type: Type.NUMBER }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async generateProposalContent(quote: Quote, settings: ProposalSettings): Promise<string> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const scopeSummary = quote.bidItems.map(i => `- ${i.title}: ${i.notes}`).join('\n');
    const extraSections = settings.includeSections.join(', ');
    const customInstruction = settings.customInstructions ? `\nIMPORTANT CLIENT REQUIREMENTS / RFP INSTRUCTIONS:\n${settings.customInstructions}\n(You MUST prioritize these instructions over the standard structure)` : '';

    const prompt = `
      Write a construction proposal cover letter.
      Client: ${quote.clientName}
      Project: ${quote.projectName}
      
      CONFIGURATION:
      - Tone: ${settings.tone}
      - Length: ${settings.length}
      - Focus: ${settings.focus}
      - Include Sections: ${extraSections}
      ${customInstruction}
      
      Scope Highlights:
      ${scopeSummary}

      Structure:
      1. Professional Greeting
      2. Executive Summary
      3. [If requested] Methodology
      4. [If requested] Safety
      5. [If requested] Bio
      6. Key Exclusions
      7. Call to Action (Valid until ${quote.dueDate})

      Format: Plain Text (with markdown for bolding).
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
      });
      return response.text.trim();
    });
  }

  async generateOutreach(customer: Customer, companyInfo: CompanyInfo, recentWins: string[]): Promise<OutreachDraft[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const winList = recentWins.length > 0 ? recentWins.join(', ') : "industrial and municipal projects";
    const targetAudience = companyInfo.targetAudience ? `Target Audience: ${companyInfo.targetAudience}` : '';
    
    const prompt = `
      You are a Sales Development Rep for ${companyInfo.name}.
      Target Client: ${customer.companyName}
      Contact: ${customer.contactName || 'The Hiring Manager'}
      Industry: ${customer.category || 'Construction/Industrial'}
      
      Company Profile:
      - Value Prop: ${companyInfo.valueProp || "Quality industrial electrical and controls services."}
      - ${targetAudience}
      - Recent Wins: ${winList}

      Generate 3 distinct outreach drafts:
      1. Cold Email (Formal introduction)
      2. LinkedIn DM (Casual, connection request style)
      3. Phone Script (For a cold call voicemail)

      Return JSON array.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                recipientName: { type: Type.STRING },
                method: { type: Type.STRING, enum: ['EMAIL', 'LINKEDIN_DM', 'PHONE_SCRIPT'] },
                subject: { type: Type.STRING },
                content: { type: Type.STRING }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async optimizeValueProp(companyInfo: CompanyInfo, targetIndustry: string): Promise<string> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const prompt = `
      Critique and improve this company's Value Proposition for the "${targetIndustry}" sector.
      
      Company: ${companyInfo.name}
      Current Value Prop: "${companyInfo.valueProp || 'General electrical contractor'}"
      Target Audience: ${companyInfo.targetAudience || 'General'}
      
      Goal: Make it punchy, authoritative, and specific to ${targetIndustry} pain points (e.g. downtime, compliance, speed).
      
      Return ONLY the improved value proposition text (1-2 sentences).
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
      });
      return response.text.trim();
    });
  }

  async scoreLead(customer: Customer, companyInfo: CompanyInfo): Promise<LeadScore> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const prompt = `
      Score this sales lead from 0 to 100 based on fit for our company.
      
      Our Company:
      - Name: ${companyInfo.name}
      - Focus: ${companyInfo.targetAudience || 'Industrial Electrical'}
      - Value Prop: ${companyInfo.valueProp}

      The Lead:
      - Name: ${customer.companyName}
      - Category: ${customer.category}
      - Location: ${customer.address}
      - Notes: ${customer.notes}

      Return JSON.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              score: { type: Type.NUMBER },
              reasoning: { type: Type.STRING },
              lastUpdated: { type: Type.STRING } // Ignore this, we set it locally
            }
          }
        }
      });
      const result = JSON.parse(response.text);
      return { ...result, lastUpdated: new Date().toISOString() };
    });
  }

  async generateCaseStudy(quote: Quote, companyInfo: CompanyInfo): Promise<CaseStudy> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const scopeSummary = quote.bidItems.map(i => `${i.title}: ${i.notes}`).join('\n');

    const prompt = `
      Write a professional case study for this completed project.
      
      Project: ${quote.projectName}
      Client: ${quote.clientName}
      Scope: 
      ${scopeSummary}
      
      Company: ${companyInfo.name}
      
      Output Requirements:
      - Title: Catchy headline
      - Challenge: The problem solved
      - Solution: Our approach/tech used
      - Outcome: The benefit to client
      - Technical Highlights: List of 3-5 key technologies or methods used.

      Return JSON.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              challenge: { type: Type.STRING },
              solution: { type: Type.STRING },
              outcome: { type: Type.STRING },
              technicalHighlights: { type: Type.ARRAY, items: { type: Type.STRING } }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async analyzeFinancialHealth(projects: { id: string; name: string; budgetHours: number; actualHours: number }[]): Promise<ProjectFinancialHealth[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const prompt = `
      Analyze these projects for financial risk based on Labor Burn Rate.
      Budget vs Actual Hours provided.
      
      Projects:
      ${JSON.stringify(projects)}

      For each project, calculate burn rate and provide a status (HEALTHY, WARNING, CRITICAL).
      CRITICAL if actual > budget.
      WARNING if actual > 80% of budget.
      HEALTHY otherwise.
      Provide short, actionable advice for the project manager.

      Return JSON Array.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                projectId: { type: Type.STRING },
                projectName: { type: Type.STRING },
                budgetHours: { type: Type.NUMBER },
                actualHours: { type: Type.NUMBER },
                burnRate: { type: Type.NUMBER },
                status: { type: Type.STRING, enum: ['HEALTHY', 'WARNING', 'CRITICAL'] },
                aiAdvice: { type: Type.STRING }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async generateCollectionEmails(invoices: Invoice[], customers: Customer[], companyInfo: CompanyInfo): Promise<OutreachDraft[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const overdueList = invoices.map(i => {
       const client = customers.find(c => c.companyName === (i as any).clientName) || { companyName: 'Client', contactName: 'Accounts Payable' };
       return `- Invoice #${i.number} for $${i.amount} to ${client.companyName} (Contact: ${client.contactName}), Due since ${i.date}`;
    }).join('\n');

    const prompt = `
      You are an Accounts Receivable specialist for ${companyInfo.name}.
      Write polite but firm overdue payment reminder emails for the following invoices.
      
      OVERDUE ITEMS:
      ${overdueList}

      For each invoice, generate one EMAIL draft.
      
      Return JSON Array of OutreachDraft objects.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                recipientName: { type: Type.STRING },
                method: { type: Type.STRING, enum: ['EMAIL'] },
                subject: { type: Type.STRING },
                content: { type: Type.STRING }
              }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }

  async analyzeMaintenanceLog(logText: string, checklists: MaintenanceChecklist[]): Promise<Partial<ServiceReport>> {
    const apiKey = this.getApiKey();
    if (!apiKey) throw new Error("AI proxy not configured");
    const ai = new GoogleGenerativeAI(apiKey);

    const checklistContext = checklists.map(c => `Checklist: "${c.name}" (ID: ${c.id}) Items: ${c.items.map(i => i.label).join(', ')}`).join('\n');

    const prompt = `
      You are a Service Manager for a water treatment plant.
      
      DAILY LOG:
      ${logText}

      AVAILABLE CHECKLISTS:
      ${checklistContext}

      TASKS:
      1. Map the log content to the relevant checklist items. If the log confirms an item is good, mark PASS. If broken, FAIL.
      2. Extract a professional summary for the "Service Report".
      3. Identify specific issues found (e.g. leaks, loose wires).
      4. Suggest recommendations.

      Return JSON.
    `;

    return this.retryOperation(async () => {
      // @ts-ignore - GoogleGenerativeAI API types may be outdated
      const response = await (ai as any).models?.generateContent({
        model: this.model,
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              summary: { type: Type.STRING },
              checklistResults: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    checklistId: { type: Type.STRING },
                    label: { type: Type.STRING }, // Use label matching if ID not clear
                    status: { type: Type.STRING, enum: ['PASS', 'FAIL', 'NA'] },
                    notes: { type: Type.STRING }
                  }
                }
              },
              issuesFound: { type: Type.ARRAY, items: { type: Type.STRING } },
              recommendations: { type: Type.ARRAY, items: { type: Type.STRING } }
            }
          }
        }
      });
      return JSON.parse(response.text);
    });
  }
}
